import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UserProfile {
  id: string;
  role: string | null;
  company_id: string | null;
}

/**
 * Loads the authenticated user's `profiles` row (role + company_id).
 *
 * Handles the race condition where the component renders before the session
 * is available: returns `{ profile: null, loading: true }` until both the
 * auth session AND the profile query have resolved. Each consumer (route
 * guards) can then make its own redirect decision based on the final values.
 *
 * Shared by ProtectedRoute, BakeryProtectedRoute, AdminProtectedRoute.
 */
export function useUserProfile() {
  const { session, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Track which userId (or "no-session" sentinel) we've actually finished
  // resolving for. `loading` is computed from comparing this to the
  // current session's userId, that prevents a stale `profile=null` from
  // being interpreted as "user has no profile" during the one-render
  // window between auth resolving and the profile query firing.
  //
  // Why this matters: ProtectedRoute reads `loading` to decide whether
  // to show a spinner vs run its redirect checks. Without this guard, a
  // fresh page load on any protected route briefly renders with
  // `loading=false` + `profile=null`, which trips the `!companyId →
  // /onboarding` branch and bounces the user to /dashboard via the
  // onboarding-completion redirect. See the regression diagnosis dated
  // 2026-05-13 against /chat for the full trace.
  const [resolvedFor, setResolvedFor] = useState<string | "no-session" | null>(null);

  useEffect(() => {
    // No session yet, either loading or unauthenticated. Mark resolved
    // as "no-session" so the consumer can inspect `session` and decide.
    if (!session?.user?.id) {
      setProfile(null);
      setResolvedFor("no-session");
      return;
    }

    let cancelled = false;
    supabase
      .from("profiles")
      .select("id, role, company_id")
      .eq("id", session.user.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        // MS447: previously dropped the error — a transient network failure
        // produced profile=null which ProtectedRoute interprets as "not yet
        // onboarded" and redirects the user to /onboarding. Log it so we
        // can see when this happens in the field; the redirect still occurs
        // (better than a permanent spinner) but it's now observable.
        if (error) {
          console.warn("MS447: profile fetch failed, user may be sent to /onboarding:", error.message);
        }
        setProfile((data as UserProfile | null) ?? null);
        setResolvedFor(session.user.id);
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const currentUserId = session?.user?.id ?? null;
  // We're still resolving when:
  //   - auth context hasn't settled yet, OR
  //   - we have a session but haven't finished a query for *this* user, OR
  //   - we have no session but haven't yet confirmed "no-session"
  const stillResolving =
    authLoading ||
    (currentUserId !== null && resolvedFor !== currentUserId) ||
    (currentUserId === null && resolvedFor !== "no-session");

  return {
    session,
    profile,
    loading: stillResolving,
  };
}
